import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { take } from 'rxjs/internal/operators/take';
import { ManagementRecordingScheduleControllerService } from 'api-clipping';
import { NavMapComponent } from '../../../../shared/components/nav-map/nav-map.component';
import { UtilsService } from '../../../../shared/services/utils.service';
import { RecordingResponse, RecordingService, SegmentationAccuracy } from '../../../../shared/services/recording.service';
import { SharedService } from '../../../../shared/services/shared.service';
import { UploadRecordingDialogComponent } from '../upload-recording-dialog/upload-recording-dialog.component';
import { EditRecordingDialogComponent } from '../edit-recording-dialog/edit-recording-dialog.component';
import { ConfirmationDialogComponent } from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';

interface MediumOption {
	id: string;
	name: string;
}

const ZONE = 'Europe/Skopje';
// While something is recording/uploading, refresh the list on its own this often.
const LIVE_REFRESH_MS = 20_000;

// "Снимки" - one row per recorded (or attempted) news edition from the server-side IPTV
// recorder, newest first, with status, reason and an inline player.
@Component({
	selector: 'app-recordings',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		MatCardModule,
		MatDividerModule,
		MatIconModule,
		MatButtonModule,
		MatTooltipModule,
		MatSelectModule,
		MatFormFieldModule,
		MatInputModule,
		MatProgressSpinnerModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatDialogModule,
		TranslateModule,
		NavMapComponent,
		RouterModule,
	],
	templateUrl: './recordings.component.html',
	styleUrl: './recordings.component.scss',
})
export class RecordingsComponent implements OnInit, OnDestroy {
	// Shown inside Settings: no page padding, card or breadcrumb (Settings has its own).
	@Input() embedded = false;

	recordings: RecordingResponse[] = [];
	mediums: MediumOption[] = [];
	loading = false;
	totalElements = 0;

	mediumId: string | null = null;
	from: Date = this.skopjeDayAsDate(-6);
	to: Date = this.skopjeDayAsDate(0);

	playing: RecordingResponse | null = null;
	accuracy: SegmentationAccuracy | null = null;

	private refreshTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(
		private recordingService: RecordingService,
		private apiRecordingSchedule: ManagementRecordingScheduleControllerService,
		private utils: UtilsService,
		private dialog: MatDialog,
		private sharedService: SharedService,
		private translate: TranslateService
	) {
		// The "Качи снимка" button lives in Settings' shared section header (same line as the
		// "Снимки" title, not inside this component's own template - see SettingsComponent), so it
		// reaches this component through the existing cross-component action channel.
		this.sharedService.requestActionInComponent.pipe(takeUntilDestroyed()).subscribe(response => {
			if (response.componentName === 'RecordingsComponent' && response.action === 'OPEN_UPLOAD_DIALOG') {
				this.openUploadDialog();
			}
		});
	}

	ngOnInit(): void {
		this.loadMediums();
		this.load();
		this.loadAccuracy();
	}

	ngOnDestroy(): void {
		this.clearTimer();
	}

	load() {
		this.clearTimer();
		this.loading = true;
		this.recordingService
			.getRecordings({ mediumId: this.mediumId, from: this.toDateString(this.from), to: this.toDateString(this.to), page: 0, size: 200 })
			.pipe(take(1))
			.subscribe({
				next: page => {
					this.recordings = page.content || [];
					this.totalElements = page.totalElements || 0;
					this.loading = false;
					if (this.playing && !this.recordings.some(r => r.id === this.playing?.id)) {
						this.playing = null;
					}
					if (
						this.recordings.some(
							r => r.status === 'RECORDING' || r.status === 'SEGMENTING' || r.status === 'UPLOADING' || r.segmentationStatus === 'RUNNING' || r.transcriptionStatus === 'RUNNING'
						)
					) {
						this.refreshTimer = setTimeout(() => this.load(), LIVE_REFRESH_MS);
					}
				},
				error: () => {
					this.loading = false;
				},
			});
	}

	percent(value: number | null | undefined): string {
		return value === null || value === undefined ? '–' : `${Math.round(value * 1000) / 10}%`;
	}

	// Accuracy over editions marked reviewed - the last 30 days, same channel filter.
	loadAccuracy() {
		const to = this.skopjeDay(0);
		const from = this.skopjeDay(-29);
		this.recordingService
			.getAccuracy({ mediumId: this.mediumId, from, to })
			.pipe(take(1))
			.subscribe({ next: accuracy => (this.accuracy = accuracy), error: () => (this.accuracy = null) });
	}

	play(recording: RecordingResponse) {
		this.playing = this.playing?.id === recording.id ? null : recording;
	}

	// Mirrors the server's own guard (ManagementRecordingServiceImpl.deleteRecording) so the
	// button is disabled while an edition is in flight - the server checks again regardless, this
	// is just so the admin doesn't have to click to find out.
	isInFlight(recording: RecordingResponse): boolean {
		return recording.status === 'RECORDING' || recording.status === 'SEGMENTING' || recording.status === 'UPLOADING' || recording.segmentationStatus === 'RUNNING';
	}

	delete(recording: RecordingResponse) {
		this.dialog
			.open(ConfirmationDialogComponent, {
				width: '500px',
				data: {
					mode: 'USER_ACTION',
					type: 'custom-fields',
					message: this.translate.instant('RECORDINGS.DELETE_CONFIRM'),
					fields: [],
				},
			})
			.afterClosed()
			.pipe(take(1))
			.subscribe(confirmed => {
				if (!confirmed) {
					return;
				}
				this.recordingService
					.deleteRecording(recording.id)
					.pipe(take(1))
					.subscribe({
						next: () => {
							if (this.playing?.id === recording.id) {
								this.playing = null;
							}
							this.load();
							this.loadAccuracy();
						},
						error: (err: HttpErrorResponse) => {
							this.sharedService.showNotification.next({
								message: err.error?.message || this.translate.instant('RECORDINGS.DELETE_ERROR'),
								type: 'ERROR',
							});
						},
					});
			});
	}

	openUploadDialog() {
		this.dialog
			.open(UploadRecordingDialogComponent, { width: '480px' })
			.afterClosed()
			.subscribe(result => {
				if (result) {
					this.load();
					this.loadAccuracy();
				}
			});
	}

	// Д1 "Уреди" - the server is the real gate (already has clips, or still processing); this
	// button is only disabled client-side while in flight, same as the delete button.
	openEditDialog(recording: RecordingResponse) {
		this.dialog
			.open(EditRecordingDialogComponent, { width: '480px', data: { recording } })
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				if (result) {
					this.load();
					this.loadAccuracy();
				}
			});
	}

	mediumName(recording: RecordingResponse): string {
		return this.utils.getParameterByLanguageScript(recording, 'mediumName') || recording.mediumNameLatin || '';
	}

	formatDay(iso: string): string {
		return new Intl.DateTimeFormat('mk-MK', { timeZone: ZONE, day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(iso));
	}

	formatTime(iso: string): string {
		return new Intl.DateTimeFormat('mk-MK', { timeZone: ZONE, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso));
	}

	formatDuration(seconds: number | null | undefined): string {
		if (seconds === null || seconds === undefined) {
			return '–';
		}
		const total = Math.round(seconds);
		return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, '0')}`;
	}

	formatSize(bytes: number | null): string {
		return bytes ? `${Math.round(bytes / 1_000_000)} MB` : '';
	}

	private loadMediums() {
		this.apiRecordingSchedule
			.getAllSchedules()
			.pipe(take(1))
			.subscribe(schedules => {
				const byId = new Map<string, MediumOption>();
				schedules.forEach(schedule => {
					if (schedule.mediumId && !byId.has(schedule.mediumId)) {
						byId.set(schedule.mediumId, {
							id: schedule.mediumId,
							name: this.utils.getParameterByLanguageScript(schedule, 'mediumName') || schedule.mediumNameLatin || '',
						});
					}
				});
				this.mediums = Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
			});
	}

	// Today (+offset days) as YYYY-MM-DD in Skopje, which is how the API reads from/to.
	private skopjeDay(offsetDays: number): string {
		const date = new Date(Date.now() + offsetDays * 86_400_000);
		return new Intl.DateTimeFormat('en-CA', { timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
	}

	// Same Skopje day, as a plain calendar Date (midnight local) for the two mat-datepicker fields.
	private skopjeDayAsDate(offsetDays: number): Date {
		const [year, month, day] = this.skopjeDay(offsetDays).split('-').map(Number);
		return new Date(year, month - 1, day);
	}

	// Reverses skopjeDayAsDate - a calendar-day pick has no timezone ambiguity to resolve, so this
	// reads the Date object's own local fields directly rather than going through Skopje again.
	private toDateString(date: Date): string {
		const year = date.getFullYear();
		const month = (date.getMonth() + 1).toString().padStart(2, '0');
		const day = date.getDate().toString().padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	private clearTimer() {
		if (this.refreshTimer) {
			clearTimeout(this.refreshTimer);
			this.refreshTimer = null;
		}
	}
}
